import { useMemo } from 'react';
import { NSDropdown } from 'bricks';
import { IOptionAnyValue } from 'bricks/types';
import useAuthContext from 'contexts/AuthContext';
import useUserSubprojects from 'views/ProFormaTable/hooks/useUserSubprojects';
import {
    ALREADY_LINKED_TO_PRO_FORMA_TOOLTIP,
    NO_MATCHING_PROJECTS_LABEL,
    PROJECT_LINK_SELECT_PLACEHOLDER,
    getProjectLinkSelectNote,
} from '../constants';

export interface IProjectLinkSelectProps {
    assetTypeCorrelationId?: string | null;
    assetTypeName: string;
    proFormaCorrelationId?: string | null;
    selectedOption?: IOptionAnyValue;
    onSelectOption: (option: IOptionAnyValue) => void;
}

const ProjectLinkSelect = ({
    assetTypeCorrelationId,
    assetTypeName,
    proFormaCorrelationId,
    selectedOption,
    onSelectOption,
}: IProjectLinkSelectProps) => {
    const { selectedOrganizationId } = useAuthContext();
    const { data: userSubprojects, isLoading } = useUserSubprojects({
        orgId: selectedOrganizationId!,
        enabled: Boolean(selectedOrganizationId),
    });

    // A still-resolving asset type reads as loading, not as "no matching projects".
    const isPickerLoading = isLoading || assetTypeCorrelationId == null;

    const options: IOptionAnyValue[] = useMemo(() => {
        if (isPickerLoading) return [];
        const matchingOptions = (userSubprojects || [])
            .filter(({ subproject }) => subproject.assetTypeCorrelationId != null
                && subproject.assetTypeCorrelationId === assetTypeCorrelationId)
            .map(({ subproject }) => {
                const isLinkedToAnotherProForma = subproject.proFormaCorrelationId != null
                    && subproject.proFormaCorrelationId !== proFormaCorrelationId;
                return {
                    label: subproject.subprojectName,
                    value: subproject.correlationId,
                    disabled: isLinkedToAnotherProForma,
                    ...(isLinkedToAnotherProForma && { tooltip: ALREADY_LINKED_TO_PRO_FORMA_TOOLTIP }),
                };
            });
        if (matchingOptions.length === 0) {
            return [{ label: NO_MATCHING_PROJECTS_LABEL, value: '', disabled: true }];
        }
        return matchingOptions;
    }, [isPickerLoading, userSubprojects, assetTypeCorrelationId, proFormaCorrelationId]);

    return (
        <>
            <div className="mb-2">{getProjectLinkSelectNote(assetTypeName)}</div>
            <NSDropdown
                options={options}
                selectedOption={selectedOption}
                handleSelectOption={onSelectOption}
                placeholder={isPickerLoading ? 'Loading projects...' : PROJECT_LINK_SELECT_PLACEHOLDER}
                isFullWidth
                dataTestId="project-link-select"
            />
        </>
    );
};

export default ProjectLinkSelect;
